import React, { useMemo, useState } from 'react';
import { createPortal } from "react-dom";

const RefExtractionModal = ({
    show,
    onClose,
    files,
    refExtractFiles,
    setRefExtractFiles
}) => {
    // Track expanded/collapsed state for each category
    const [expandedCategories, setExpandedCategories] = useState({});

    // File categories configuration with custom CSS category styles
    const fileCategories = {
        pdfFiles: { 
            label: 'PDF Documents', 
            icon: 'bi-file-earmark-pdf', 
            categoryNum: 5  // Uses --category-bg-5, --category-text-5 (pink/danger)
        },
        wordFiles: { 
            label: 'Word Documents', 
            icon: 'bi-file-earmark-word', 
            categoryNum: 2  // Uses --category-bg-2, --category-text-2 (blue)
        },
        textFiles: { 
            label: 'Text Documents', 
            icon: 'bi-file-earmark-text', 
            categoryNum: 4  // Uses --category-bg-4, --category-text-4 (purple)
        },
        pptFiles: { 
            label: 'PowerPoint Documents', 
            icon: 'bi-file-earmark-ppt', 
            categoryNum: 3  // Uses --category-bg-3, --category-text-3 (orange)
        },
        excelFiles: { 
            label: 'Excel Documents', 
            icon: 'bi-file-earmark-excel', 
            categoryNum: 1  // Uses --category-bg-1, --category-text-1 (green)
        },
        csvFiles: { 
            label: 'CSV Documents', 
            icon: 'bi-filetype-csv', 
            categoryNum: 6  // Uses --category-bg-6, --category-text-6 (teal)
        }
    };

    // Get all uploaded files grouped by category
    const groupedFiles = useMemo(() => {
        const groups = {};
        let totalCount = 0;

        Object.entries(files).forEach(([category, categoryFiles]) => {
            if (categoryFiles && categoryFiles.length > 0) {
                groups[category] = categoryFiles.map((item, index) => ({
    id: item.id,
    name: item.file.name,
    size: (item.file.size / (1024 * 1024)).toFixed(2),
    index
}));
                totalCount += categoryFiles.length;
            }
        });

        return { groups, totalCount };
    }, [files]);

    // Initialize expanded state for all categories (default: expanded)
    useMemo(() => {
        const initialExpanded = {};
        Object.keys(groupedFiles.groups).forEach(category => {
            if (expandedCategories[category] === undefined) {
                initialExpanded[category] = true;
            }
        });
        if (Object.keys(initialExpanded).length > 0) {
            setExpandedCategories(prev => ({ ...prev, ...initialExpanded }));
        }
    }, [groupedFiles.groups]);

    // Toggle category expand/collapse
    const toggleCategoryExpand = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    // Expand/Collapse all categories
    const toggleAllExpand = (expand) => {
        const newState = {};
        Object.keys(groupedFiles.groups).forEach(category => {
            newState[category] = expand;
        });
        setExpandedCategories(newState);
    };

    // Toggle reference extraction for a file
    const toggleRefExtract = (fileId) => {
        setRefExtractFiles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fileId)) newSet.delete(fileId);
            else newSet.add(fileId);
            return newSet;
        });
    };

    // Select/Deselect all files in a category
    const toggleCategorySelection = (fileIds, e) => {
        e.stopPropagation(); // Prevent triggering expand/collapse
        const allSelected = fileIds.every(id => refExtractFiles.has(id));
        setRefExtractFiles(prev => {
            const newSet = new Set(prev);
            if (allSelected) fileIds.forEach(id => newSet.delete(id));
            else fileIds.forEach(id => newSet.add(id));
            return newSet;
        });
    };

    // Select/Deselect all files
    const toggleAllRefExtract = () => {
        const allFileIds = Object.values(groupedFiles.groups).flat().map(f => f.id);
        if (refExtractFiles.size === allFileIds.length) {
            setRefExtractFiles(new Set());
        } else {
            setRefExtractFiles(new Set(allFileIds));
        }
    };

    const renderCategoryCard = (category, categoryFiles) => {
        const categoryInfo = fileCategories[category];
        const categoryFileIds = categoryFiles.map(f => f.id);
        const isExpanded = expandedCategories[category] !== false;

        const allSelected =
            categoryFileIds.length > 0 &&
            categoryFileIds.every(id => refExtractFiles.has(id));

        const someSelected =
            categoryFileIds.some(id => refExtractFiles.has(id)) && !allSelected;

        const selectedCount = categoryFileIds.filter(id => refExtractFiles.has(id)).length;

        return (
            <div key={category} className={`ev-card-4 ev-radius-small shadow-sm`}>
                {/* Header - Clickable for expand/collapse */}
                <div
                    className={`d-flex align-items-center justify-content-between px-3 py-2 category-badge category-badge-${categoryInfo.categoryNum}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleCategoryExpand(category)}
                >
                    <div className="d-flex align-items-center gap-2">
                        <input
                            type="checkbox"
                            className="form-check-input mb-1"
                            checked={allSelected}
                            ref={el => {
                                if (el) el.indeterminate = someSelected;
                            }}
                            onChange={(e) => toggleCategorySelection(categoryFileIds, e)}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <i className={`bi ${categoryInfo.icon}`}></i>
                        <span className="ev-bold-med ev-fs-12">
                            {categoryInfo.label}
                        </span>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <span className="ev-fs-11">
                            {selectedCount}/{categoryFiles.length} selected
                        </span>
                        <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </div>
                </div>

                {/* Document list - Collapsible */}
                {isExpanded && (
                    <div className="px-2 py-1">
                        {categoryFiles.map((file, idx) => (
                            <div
                                key={file.id}
                                className={`d-flex align-items-center justify-content-between ev-message-card1 mb-1 ${idx < categoryFiles.length - 1 ? 'border-bottom' : ''}`}
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <input
                                        type="checkbox"
                                        className="form-check-input mb-1"
                                        checked={refExtractFiles.has(file.id)}
                                        onChange={() => toggleRefExtract(file.id)}
                                    />
                                    <span
                                        className="ev-reg-p-dark text-truncate ev-fs-12"
                                        style={{ maxWidth: '380px' }}
                                        title={file.name}
                                    >
                                        {file.name}
                                    </span>
                                </div>

                                <span className="ev-small-p-blue ev-fs-11">
                                    {file.size} MB
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (!show) return null;

    const allExpanded = Object.values(expandedCategories).every(v => v === true);

    return createPortal(
        <div className="modal d-block bg-dark bg-opacity-50" style={{ zIndex: "var(--num-1000000000)" }}>
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                <div className="modal-content ev-card-3">
                    {/* Modal Header */}
                    <div className="d-flex justify-content-between align-items-center modal-header border-0 pb-2">
                        <h6 className="ev-med-p-dark m-0">
                            <i className="bi bi-bookmark-check me-2"></i>
                            Select Documents for Reference Extraction
                        </h6>
                        <button
                            type="button"
                            className="ev-btn-text-only-fill ev-fs-20"
                            onClick={onClose}
                        >
                            <i className="bi bi-x"></i>
                        </button>
                    </div>

                    {/* Modal Body */}
                    <div className="modal-body p-3 pt-0">
                        {groupedFiles.totalCount === 0 ? (
                            <div className="text-center py-4">
                                <i className="bi bi-folder2-open text-muted ev-fs-40"></i>
                                <p className="text-muted mt-2 ev-fs-12">
                                    No documents uploaded yet. Please upload documents first.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="ev-message-card2 mb-3">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Select documents whose references you want to extract and store.
                                </div>

                                {/* Select All Header */}
                                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                    <div className="d-flex align-items-center gap-2">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={
                                                refExtractFiles.size === groupedFiles.totalCount &&
                                                groupedFiles.totalCount > 0
                                            }
                                            onChange={toggleAllRefExtract}
                                        />
                                        <span className="ev-reg-p-dark ev-bold-med">
                                            Select All ({refExtractFiles.size}/{groupedFiles.totalCount})
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="ev-btn-blue-outline-round ev-btn-xsmall"
                                        onClick={() => toggleAllExpand(!allExpanded)}
                                    >
                                        <i className={`bi ${allExpanded ? 'bi-arrows-collapse' : 'bi-arrows-expand'} me-1`}></i>
                                        {allExpanded ? 'Collapse All' : 'Expand All'}
                                    </button>
                                </div>

                                <div className="d-flex flex-column gap-2 scrollable-element vh-45 pe-2">
                                    {Object.entries(groupedFiles.groups).map(([category, categoryFiles]) =>
                                        renderCategoryCard(category, categoryFiles)
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Modal Footer */}
                    <div className="modal-footer border-0 pt-0">
                        <button
                            type="button"
                            className="ev-btn-blue-fill-round ev-btn-reg"
                            onClick={onClose}
                        >
                            <i className="bi bi-check-circle me-1"></i>
                            Confirm Selection
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RefExtractionModal;